import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import isEmail from 'validator/lib/isEmail';

import logo from '../../assets/images/logo.png';
import { APP_NAME } from '../../constants/Brand';
import { SsoTypes } from '../../constants/Enums';
import { useForm } from '../../hooks';
import { useDidUpdate, usePrevious, useToggle } from '../../lib/hooks';
import { isUsername } from '../../utils/validator';
import { Button, ButtonVariant, Icon, IconType, IconSize, Input, InputVariant, Form, Message, MessageVariant, Loader, LoaderSize } from '../Utils';

import * as gs from '../../global.module.scss';
import * as auth from '../Auth/AuthLayout.module.scss';

const createMessage = (error) => {
  if (!error) {
    return error;
  }

  switch (error.message) {
    case 'Invalid username or password':
      return {
        type: 'error',
        content: 'error.invalidUsernameOrPassword',
      };
    case 'tooManyFailedAttempts':
      return {
        type: 'error',
        content: 'error.tooManyFailedAttempts',
      };
    case 'registrationDisabled':
      return {
        type: 'error',
        content: 'error.registrationDisabled',
      };
    case 'ssoRegistrationDisabled':
      return {
        type: 'error',
        content: 'error.ssoRegistrationDisabled',
      };
    case 'coreNotFound':
      return {
        type: 'error',
        content: 'error.coreNotFound',
      };
    case 'Failed to fetch':
      return {
        type: 'warning',
        content: 'error.noInternetConnection',
      };
    case 'Network request failed':
      return {
        type: 'warning',
        content: 'error.serverConnectionFailed',
      };
    case 'noOidcProfileFound':
      return {
        type: 'error',
        content: 'error.noOidcProfileFound',
      };
    case 'domainNotAllowed':
      return {
        type: 'error',
        content: 'error.domainNotAllowed',
      };
    default:
      return {
        type: 'warning',
        content: 'error.unknownError',
      };
  }
};

const SSO_PROVIDERS = [
  { type: SsoTypes.GOOGLE, label: 'Google', icon: IconType.Google },
  { type: SsoTypes.MICROSOFT, label: 'Microsoft', icon: IconType.Microsoft },
  { type: SsoTypes.GITHUB, label: 'GitHub', icon: IconType.GitHub },
  { type: SsoTypes.OIDC, label: 'OIDC', icon: IconType.Key },
];

const Login = React.memo(
  ({
    defaultData,
    isSubmitting,
    error,
    ssoAvailable,
    oidcEnabledMethods,
    registrationEnabled,
    localRegistrationEnabled,
    ssoRegistrationEnabled,
    onAuthenticate,
    onAuthenticateSso,
    onMessageDismiss,
    onRegisterOpen,
  }) => {
    const [t] = useTranslation();
    const [isUsernameError, setIsUsernameError] = useState(false);
    const [isPasswordError, setIsPasswordError] = useState(false);
    const [loadingProvider, setLoadingProvider] = useState(null);
    const wasSubmitting = usePrevious(isSubmitting);
    const [data, handleFieldChange, setData] = useForm(() => ({
      emailOrUsername: '',
      password: '',
      ...defaultData,
    }));

    const message = useMemo(() => createMessage(error), [error]);
    const [focusPasswordFieldState, focusPasswordField] = useToggle();

    const emailOrUsernameField = useRef(null);
    const passwordField = useRef(null);

    useEffect(() => {
      document.title = `${t('common.login')} | ${APP_NAME}`;
    }, [t]);

    const handleSubmit = useCallback(() => {
      const cleanData = {
        ...data,
        emailOrUsername: data.emailOrUsername.trim(),
      };

      if (!isEmail(cleanData.emailOrUsername) && !isUsername(cleanData.emailOrUsername)) {
        emailOrUsernameField.current?.focus();
        setIsUsernameError(true);
        return;
      }

      if (!cleanData.password) {
        passwordField.current?.focus();
        setIsPasswordError(true);
        return;
      }

      setLoadingProvider('local');
      onAuthenticate(cleanData);
    }, [onAuthenticate, data]);

    useEffect(() => {
      if (error) {
        setLoadingProvider(null);
      }
    }, [error]);

    const handleSsoClick = useCallback(
      async (provider, method = null) => {
        const id = method ? `${provider}:${method}` : provider;
        setLoadingProvider(id);
        onAuthenticateSso(provider, method);
      },
      [onAuthenticateSso],
    );

    useEffect(() => {
      emailOrUsernameField.current?.focus();
    }, []);

    useEffect(() => {
      if (wasSubmitting && !isSubmitting && error) {
        switch (error.message) {
          case 'Invalid username or password':
            focusPasswordField();
            setIsUsernameError(true);
            setIsPasswordError(true);
            break;
          default:
        }
      }
    }, [isSubmitting, wasSubmitting, error, setData, focusPasswordField]);

    const handleUsernameKeyDown = useCallback(() => {
      if (isUsernameError) {
        setIsUsernameError(false);
        onMessageDismiss();
      }
    }, [isUsernameError, onMessageDismiss]);

    const handlePasswordKeyDown = useCallback(() => {
      if (isPasswordError) {
        setIsPasswordError(false);
        onMessageDismiss();
      }
    }, [isPasswordError, onMessageDismiss]);

    useDidUpdate(() => {
      passwordField.current?.focus();
    }, [focusPasswordFieldState]);

    return (
      <div className={clsx(auth.wrapper, auth.loginPage)}>
        <aside className={clsx(auth.brandPanel, auth.loginBrandPanel)} aria-hidden="true">
          <div className={auth.ambientGlowOne} />
          <div className={auth.ambientGlowTwo} />
          <div className={auth.brandGrid} />
          <div className={auth.brandContent}>
            <div className={auth.brandLogoShell}>
              <img src={logo} alt="" className={auth.brandLogo} />
            </div>
            <div className={auth.brandEyebrow}>Your team, in sync</div>
            <h2 className={auth.brandTitle}>{APP_NAME}</h2>
            <p className={auth.brandSubtitle}>Plan clearly. Collaborate naturally. Deliver meaningful work together.</p>
            <div className={auth.productPreview}>
              <div className={auth.previewHeader}>
                <span />
                <span />
                <span />
              </div>
              <div className={auth.previewBody}>
                <div className={auth.previewSidebar} />
                <div className={auth.previewColumn}>
                  <i />
                  <i />
                  <i />
                </div>
                <div className={auth.previewColumn}>
                  <i />
                  <i />
                </div>
                <div className={auth.previewColumn}>
                  <i />
                  <i />
                  <i />
                </div>
              </div>
            </div>
          </div>
          <p className={auth.brandFooter}>© {new Date().getFullYear()} {APP_NAME}</p>
        </aside>
        <main className={clsx(auth.formPanel, auth.loginFormPanel, gs.scrollableY)}>
          <div className={auth.formGlow} aria-hidden="true" />
          <div className={auth.loginWrapper}>
            <img src={logo} className={auth.logo} alt={APP_NAME} />
            <div className={auth.formEyebrow}>{APP_NAME}</div>
            <h1 className={auth.formTitle}>{t('common.logInToBoards')}</h1>
            <div>
              {message && <Message variant={message.type === 'error' ? MessageVariant.Error : MessageVariant.Warning} content={t(message.content)} onDismiss={onMessageDismiss} className={auth.message} />}
              <Form>
                <div className={auth.inputLabel}>{t('common.emailOrUsername')}</div>
                <Input
                  ref={emailOrUsernameField}
                  variant={InputVariant.LoginRegister}
                  name="emailOrUsername"
                  value={data.emailOrUsername}
                  readOnly={isSubmitting}
                  onKeyDown={handleUsernameKeyDown}
                  onChange={handleFieldChange}
                  isError={isUsernameError}
                />
                <div className={auth.inputLabel}>{t('common.password')}</div>
                <Input.Password
                  ref={passwordField}
                  variant={InputVariant.LoginRegister}
                  name="password"
                  value={data.password}
                  readOnly={isSubmitting}
                  onKeyDown={handlePasswordKeyDown}
                  onChange={handleFieldChange}
                  isError={isPasswordError}
                />
                <Button variant={ButtonVariant.Login} type="submit" title={t('common.logIn')} disabled={isSubmitting} className={clsx(auth.submitButton, auth.button)} onClick={handleSubmit}>
                  {loadingProvider === 'local' ? (
                    <Loader size={LoaderSize.Small} />
                  ) : (
                    <>
                      {t('common.logIn')}
                      <Icon type={IconType.ArrowDown} size={IconSize.Size20} className={auth.submitButtonIcon} />
                    </>
                  )}
                </Button>
              </Form>
              {Object.values(ssoAvailable).some(Boolean) && (
                <>
                  <div className={auth.otherOptionsTextWrapper}>
                    <div className={auth.otherOptionsLine} />
                    <div className={auth.otherOptionsText}>{t('common.or')}</div>
                    <div className={auth.otherOptionsLine} />
                  </div>
                  <div className={auth.otherOptions}>
                    {SSO_PROVIDERS.filter((p) => ssoAvailable[p.type] && (p.type !== SsoTypes.OIDC || oidcEnabledMethods.length === 0)).map((p) => (
                      <Button key={p.type} variant={ButtonVariant.Login} title={t('common.continueWith', { provider: p.label })} onClick={() => handleSsoClick(p.type)} className={auth.button}>
                        {loadingProvider === p.type ? (
                          <Loader size={LoaderSize.Small} />
                        ) : (
                          <>
                            <Icon type={p.icon} size={IconSize.Size20} className={auth.ssoIcon} />
                            {t('common.continueWith', { provider: p.label })}
                          </>
                        )}
                      </Button>
                    ))}
                    {ssoAvailable[SsoTypes.OIDC] &&
                      oidcEnabledMethods.length > 0 &&
                      oidcEnabledMethods.map((method) => {
                        const id = `${SsoTypes.OIDC}:${method}`;
                        return (
                          <Button key={id} variant={ButtonVariant.Login} title={t('common.continueWith', { provider: method })} onClick={() => handleSsoClick(SsoTypes.OIDC, method)} className={auth.button}>
                            {loadingProvider === id ? (
                              <Loader size={LoaderSize.Small} />
                            ) : (
                              <>
                                <Icon type={IconType[method] || IconType.Key} size={IconSize.Size20} className={auth.ssoIcon} />
                                {t('common.continueWith', { provider: method })}
                              </>
                            )}
                          </Button>
                        );
                      })}
                  </div>
                </>
              )}
              {registrationEnabled && (localRegistrationEnabled || ssoRegistrationEnabled) && (
                <>
                  <div className={auth.alternateActionText}>{t('common.newToBoards')}</div>
                  <div className={auth.alternateActionButtonContainer}>
                    <Button variant={ButtonVariant.Login} content={t('common.createAccount')} onClick={onRegisterOpen} />
                  </div>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  },
);

Login.propTypes = {
  defaultData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isSubmitting: PropTypes.bool.isRequired,
  error: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  ssoAvailable: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  oidcEnabledMethods: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  registrationEnabled: PropTypes.bool.isRequired,
  localRegistrationEnabled: PropTypes.bool.isRequired,
  ssoRegistrationEnabled: PropTypes.bool.isRequired,
  onAuthenticate: PropTypes.func.isRequired,
  onAuthenticateSso: PropTypes.func.isRequired,
  onMessageDismiss: PropTypes.func.isRequired,
  onRegisterOpen: PropTypes.func.isRequired,
};

Login.defaultProps = {
  error: undefined,
};

export default Login;
